import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function Register() {
  const { register, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refCode = params.get("ref") || "";
  const [form, setForm] = useState({ full_name: "", email: "", password: "", gender: "male" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ ...form, ref: refCode || undefined });
      navigate("/profile/me?onboarding=1");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 md:px-12 py-16" data-testid="register-page">
      <form onSubmit={submit} className="w-full max-w-lg">
        <p className="overline mb-3">Create profile</p>
        <h1 className="font-serif-display text-4xl mb-3 leading-tight">A few details to begin.</h1>
        <p className="text-sm text-[#8C857B] mb-10">You can fill in the rest of your story next.</p>

        {error && (
          <div data-testid="register-error" className="mb-6 rounded-xl bg-[#FAE5E0] border border-[#9C3A3A]/20 text-[#9C3A3A] px-4 py-3 text-sm">
            {error}
          </div>
        )}
        {refCode && (
          <div className="mb-6 rounded-xl bg-[#E8F0EA] border border-[#2E5C46]/20 text-[#2E5C46] px-4 py-3 text-sm" data-testid="referral-banner">
            🎁 You're joining via a referral code <strong>{refCode}</strong> — both you and your friend get 30 days Premium when you upgrade.
          </div>
        )}

        <label className="block text-sm mb-2">Full name</label>
        <input required value={form.full_name} onChange={update("full_name")} data-testid="register-name-input"
          className="w-full rounded-xl border border-[#E2DCD0] bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C85A44]/30 transition mb-5" />

        <label className="block text-sm mb-2">Email</label>
        <input required type="email" value={form.email} onChange={update("email")} data-testid="register-email-input"
          className="w-full rounded-xl border border-[#E2DCD0] bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C85A44]/30 transition mb-5" />

        <label className="block text-sm mb-2">Password</label>
        <input required type="password" minLength={6} value={form.password} onChange={update("password")} data-testid="register-password-input"
          className="w-full rounded-xl border border-[#E2DCD0] bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C85A44]/30 transition mb-5" />

        <label className="block text-sm mb-2">I am a</label>
        <div className="grid grid-cols-2 gap-3 mb-8">
          {["male", "female"].map((g) => (
            <button type="button" key={g} onClick={() => setForm((s) => ({ ...s, gender: g }))}
              data-testid={`register-gender-${g}`}
              className={`rounded-xl border px-4 py-3 text-sm transition ${
                form.gender === g
                  ? "border-[#C85A44] bg-[#C85A44] text-white"
                  : "border-[#E2DCD0] bg-white hover:bg-[#F2EFE9]"
              }`}>
              {g === "male" ? "Man" : "Woman"}
            </button>
          ))}
        </div>

        <button type="submit" disabled={loading} data-testid="register-submit-button"
          className="w-full rounded-full bg-[#C85A44] hover:bg-[#A84A36] disabled:opacity-60 text-white px-6 py-3.5 text-sm font-medium transition">
          {loading ? "Creating…" : "Create account"}
        </button>
        <p className="mt-6 text-sm text-[#8C857B] text-center">
          Already a member?{" "}
          <Link to="/login" data-testid="register-to-login" className="text-[#C85A44] hover:underline">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
